import { BaseTeam } from "@redotech/account-common/base-team";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Card } from "@redotech/arbiter/card";
import { Flex } from "@redotech/arbiter/flex";
import { RedoIncrementDecrement } from "@redotech/arbiter/increment-decrement/redo-increment-decrement";
import { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/arbiter/select-dropdown/redo-single-select-dropdown-input";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { CollectionMethod } from "@redotech/redo-billing-common/billing-engine-types";
import {
  cadenceAnchorsOnDayOfMonth,
  MAX_DAY_OF_MONTH,
  MAX_NET_DAYS,
  MIN_DAY_OF_MONTH,
  type NetTermsSettings as NetTermsSettingsModel,
  validateNetTermsSettings,
} from "@redotech/redo-billing-common/net-terms";
import { BillingPeriod } from "@redotech/redo-billing-common/service-invoice";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { titleCase } from "@redotech/web-util/strings";
import { memo, useEffect, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

/** The cadence the form edits. `enabled` is not here: only the cutover sets it. */
export const netTermsSettingsForm = groupInput({
  cadence: input<BillingPeriod>(),
  dayOfWeek: input<number>(),
  dayOfMonth: input<number>(),
  collectionMethod: input<CollectionMethod>(),
  netTermsDays: input<number>(),
});

export type NetTermsSettingsForm = InputSchema.Input<
  typeof netTermsSettingsForm
>;

export type NetTermsSettingsValue = InputSchema.Value<
  typeof netTermsSettingsForm
>;

export const netTermsSettingsDefaultValue: NetTermsSettingsValue = {
  cadence: BillingPeriod.MONTHLY,
  dayOfWeek: 1,
  dayOfMonth: 1,
  collectionMethod: CollectionMethod.AUTO,
  netTermsDays: 7,
};

/**
 * The stored shape leaves the day fields unset for cadences that do not use
 * them; the form always holds a concrete number, so fall back to the defaults.
 */
export function netTermsValueFromSettings(
  netTerms: NetTermsSettingsModel | undefined,
): NetTermsSettingsValue {
  return {
    cadence: netTerms?.cadence ?? netTermsSettingsDefaultValue.cadence,
    dayOfWeek: netTerms?.dayOfWeek ?? netTermsSettingsDefaultValue.dayOfWeek,
    dayOfMonth: netTerms?.dayOfMonth ?? netTermsSettingsDefaultValue.dayOfMonth,
    collectionMethod:
      netTerms?.collectionMethod ??
      netTermsSettingsDefaultValue.collectionMethod,
    netTermsDays:
      netTerms?.netTermsDays ?? netTermsSettingsDefaultValue.netTermsDays,
  };
}

const cadenceLabels: Record<BillingPeriod, string> = {
  [BillingPeriod.WEEKLY]: "Weekly",
  [BillingPeriod.BIMONTHLY]: "Twice a month",
  [BillingPeriod.MONTHLY]: "Monthly",
  [BillingPeriod.YEARLY]: "Yearly",
};

const cadenceOptions: RedoListItem<BillingPeriod>[] = Object.values(
  BillingPeriod,
).map((cadence) => ({
  id: cadence,
  type: "text" as const,
  text: cadenceLabels[cadence],
  value: cadence,
}));

const collectionMethodOptions: RedoListItem<CollectionMethod>[] = [
  {
    id: CollectionMethod.AUTO,
    type: "text" as const,
    text: "Charge automatically when due",
    value: CollectionMethod.AUTO,
  },
  {
    id: CollectionMethod.MANUAL,
    type: "text" as const,
    text: "Email the merchant to pay",
    value: CollectionMethod.MANUAL,
  },
];

const dayOfWeekNames = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const dayOfWeekOptions: RedoListItem<number>[] = dayOfWeekNames.map(
  (name, index) => ({
    id: name,
    type: "text" as const,
    text: titleCase(name),
    value: index,
  }),
);

export const NetTermsSettings = memo(function NetTermsSettings({
  netTermsInput,
  team,
  enabled,
}: {
  netTermsInput: NetTermsSettingsForm;
  team: BaseTeam | undefined;
  /** Whether the merchant is on terms today, as stored; written only by the cutover. */
  enabled: boolean;
}) {
  const { cadence, dayOfWeek, dayOfMonth, collectionMethod, netTermsDays } =
    netTermsInput.inputs;

  const validationError = useMemo(
    () =>
      validateNetTermsSettings({
        enabled: true,
        cadence: cadence.value,
        dayOfWeek: dayOfWeek.value,
        dayOfMonth: dayOfMonth.value,
        collectionMethod: collectionMethod.value,
        netTermsDays: netTermsDays.value,
      }),
    [
      cadence.value,
      dayOfWeek.value,
      dayOfMonth.value,
      collectionMethod.value,
      netTermsDays.value,
    ],
  );

  return (
    <Card title="Net terms billing">
      <Flex flexDirection="column">
        <Text fontSize="sm" textColor="secondary">
          Invoice accumulated label charges on a cadence and collect when due,
          instead of charging each label against the balance. The debt cap stays
          the minimum balance above. Save the cadence here, then start net terms
          below to move the merchant onto it.
        </Text>
        <Text fontSize="sm" fontWeight="semibold">
          {enabled ? "Net terms: on" : "Net terms: off"}
        </Text>

        <Flex align="center" flexDirection="row">
          <Text>Billing cadence</Text>
          <RedoSingleSelectDropdownInput<BillingPeriod>
            options={cadenceOptions}
            optionSelected={({ item }) => cadence.setValue(item.value)}
            selectedItem={cadenceOptions.find(
              (option) => option.value === cadence.value,
            )}
            size="xs"
          />
        </Flex>

        {!cadenceAnchorsOnDayOfMonth(cadence.value) && (
          <Flex align="center" flexDirection="row">
            <Text>Invoice on</Text>
            <RedoSingleSelectDropdownInput<number>
              options={dayOfWeekOptions}
              optionSelected={({ item }) => dayOfWeek.setValue(item.value)}
              selectedItem={dayOfWeekOptions.find(
                (option) => option.value === dayOfWeek.value,
              )}
              size="xs"
            />
          </Flex>
        )}

        {cadenceAnchorsOnDayOfMonth(cadence.value) && (
          <Flex align="center" flexDirection="row">
            <Text>Invoice on day of month</Text>
            <RedoIncrementDecrement
              max={MAX_DAY_OF_MONTH}
              min={MIN_DAY_OF_MONTH}
              placeholder="1"
              setValue={(value) => dayOfMonth.setValue(value ?? 1)}
              size="sm"
              value={dayOfMonth.value ?? 1}
            />
          </Flex>
        )}

        <Flex align="center" flexDirection="row">
          <Text>Collection method</Text>
          <RedoSingleSelectDropdownInput<CollectionMethod>
            options={collectionMethodOptions}
            optionSelected={({ item }) => collectionMethod.setValue(item.value)}
            selectedItem={collectionMethodOptions.find(
              (option) => option.value === collectionMethod.value,
            )}
            size="xs"
          />
        </Flex>

        <Flex align="center" flexDirection="row">
          <Text>Net period (days)</Text>
          <RedoIncrementDecrement
            max={MAX_NET_DAYS}
            min={1}
            placeholder="7"
            setValue={(value) => netTermsDays.setValue(value ?? 1)}
            size="sm"
            value={netTermsDays.value ?? 1}
          />
        </Flex>
        <Text fontSize="sm" textColor="secondary">
          Cadence and net period are independent — weekly billing on net 7 bills
          every week with each invoice due 7 days after it is issued. Yearly
          repeats in the month the cycle started.
        </Text>

        {validationError && (
          <Text fontSize="sm" textColor="error">
            {validationError}
          </Text>
        )}

        <NetTermsCutover
          enabled={enabled}
          team={team}
          unsavedChanges={netTermsInput.changed}
        />
      </Flex>
    </Card>
  );
});

/** How often to ask after the cutover while it is still running. */
const STATUS_POLL_MS = 3000;

/**
 * Starts (or stops) net terms. Starting runs the `startNetTerms` workflow:
 * refund the wallet credit, invoice what is owed, open the first period, and
 * only then flip the setting on. The readout below follows the workflow.
 */
const NetTermsCutover = memo(function NetTermsCutover({
  team,
  enabled,
  unsavedChanges,
}: {
  team: BaseTeam | undefined;
  enabled: boolean;
  /** The cutover reads the saved cadence, so it must not start over unsaved edits. */
  unsavedChanges: boolean;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const teamId = team?._id;

  const [statusNonce, setStatusNonce] = useState(0);
  const statusLoad = useLoad(
    async (signal) =>
      teamId
        ? await rpcClient.getNetTermsCutoverStatus({ teamId }, { signal })
        : undefined,
    [rpcClient, teamId, statusNonce],
  );
  const status = statusLoad.value;

  useEffect(() => {
    if (!status?.running) {
      return;
    }
    const timer = setTimeout(
      () => setStatusNonce((nonce) => nonce + 1),
      STATUS_POLL_MS,
    );
    return () => clearTimeout(timer);
  }, [status?.running, statusNonce]);

  const [startLoad, start] = useTriggerLoad(async (signal) => {
    if (!teamId) {
      return undefined;
    }
    const started = await rpcClient.startNetTerms({ teamId }, { signal });
    setStatusNonce((nonce) => nonce + 1);
    return started;
  });

  const [stopLoad, stop] = useTriggerLoad(async (signal) => {
    if (!teamId) {
      return undefined;
    }
    await rpcClient.stopNetTerms({ teamId }, { signal });
    return true;
  });

  const result = status?.result;

  return (
    <Flex flexDirection="column" pt="md">
      <Text fontWeight="semibold">
        {enabled ? "Stop net terms" : "Start net terms"}
      </Text>
      <Text fontSize="sm" textColor="secondary">
        {enabled
          ? "Turning terms off returns the merchant to the prepaid balance from their next label. Their unpaid invoices are still collected."
          : "Two steps: save the cadence above with the page's Save button, then start. Starting refunds the remaining balance to the cards that funded it, invoices what the merchant owes on the saved terms, opens their first period, and then turns net terms on."}
      </Text>
      {!enabled && unsavedChanges && (
        <Text fontSize="sm" textColor="warning">
          Save your changes to the cadence first — Start uses the saved
          settings, not what is in the form.
        </Text>
      )}
      <Flex>
        {enabled ? (
          <RedoButton
            disabled={!team}
            hierarchy="secondary"
            onClick={() => stop()}
            pending={stopLoad.pending}
            size="sm"
            text="Stop net terms"
            theme="destructive"
          />
        ) : (
          <RedoButton
            disabled={!team || unsavedChanges || status?.running}
            hierarchy="secondary"
            onClick={() => start()}
            pending={startLoad.pending}
            size="sm"
            text="Start net terms"
            theme="normal"
          />
        )}
      </Flex>
      {stopLoad.value && (
        <Text fontSize="sm">Net terms stopped. Reload to see the change.</Text>
      )}
      {(startLoad.error ?? stopLoad.error) != null && (
        <Text fontSize="sm" textColor="error">
          {String(startLoad.error ?? stopLoad.error)}
        </Text>
      )}
      {startLoad.value?.alreadyRunning && (
        <Text fontSize="sm">A cutover is already running for this team.</Text>
      )}
      {status?.running && (
        <Text fontSize="sm" textColor="secondary">
          Cutover running — refunding, invoicing, opening the first period…
        </Text>
      )}
      {status?.failed && (
        <Text fontSize="sm" textColor="error">
          The cutover failed. Check the workflow in Temporal before retrying;
          any refund it issued has already been debited from the balance.
        </Text>
      )}
      {result && !status?.running && (
        <Flex flexDirection="column" gap="xs">
          {result.alreadyOnTerms ? (
            <Text fontSize="sm">Already on net terms — nothing moved.</Text>
          ) : (
            <>
              <Text fontSize="sm">
                {`Refunded $${result.refunded.toFixed(2)} to the merchant's cards.`}
              </Text>
              {result.refundRemainder > 0 && (
                <Text fontSize="sm" textColor="error">
                  {`$${result.refundRemainder.toFixed(2)} of credit could not be refunded to a past top-up and is still on the balance. Refund it in Stripe and deduct it above.`}
                </Text>
              )}
              <Text fontSize="sm">
                {result.invoice
                  ? `Invoiced $${result.invoice.amount}, due ${new Date(result.invoice.dueDate).toLocaleDateString()}.`
                  : "Nothing was owed, so no invoice was raised."}
              </Text>
            </>
          )}
          <Text fontSize="sm">
            {`Current period: ${new Date(result.periodStart).toLocaleDateString()} to ${new Date(result.periodEnd).toLocaleDateString()}.`}
          </Text>
        </Flex>
      )}
    </Flex>
  );
});
